import React, { useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { useField, useFormikContext } from 'formik';
import styled from 'styled-components';
import { TimesIcon } from '@patternfly/react-icons';
import {
  Button,
  Divider,
  FileUpload,
  Flex,
  FlexItem,
  FormGroup,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
} from '@patternfly/react-core';
import { useConfig } from 'contexts/Config';
import getDocsBaseUrl from 'util/getDocsBaseUrl';
import useModal from 'hooks/useModal';
import FormField, { PasswordField } from 'components/FormField';
import Popover from 'components/Popover';
import SubscriptionModal from './SubscriptionModal';

const LICENSELINK = 'https://www.ansible.com/license';
const FileUploadField = styled(FormGroup)`
  && {
    max-width: 500px;
    width: 100%;
  }
`;

function SubscriptionStep() {
  const config = useConfig();
  const hasValidKey = Boolean(config?.license_info?.valid_key);

  const { values } = useFormikContext();

  const [isSelected, setIsSelected] = useState(
    values.subscription ? 'selectSubscription' : 'uploadManifest'
  );
  const [subscriptionMethod, , subscriptionMethodHelpers] = useField(
    'subscription_method'
  );
  const { isModalOpen, toggleModal, closeModal } = useModal();
  const [manifest, manifestMeta, manifestHelpers] = useField('manifest_file');
  const [manifestFilename, , manifestFilenameHelpers] =
    useField('manifest_filename');
  const [subscription, , subscriptionHelpers] = useField('subscription');
  const [clientId, clientIdMeta, clientIdHelpers] = useField('client_id');
  const [clientSecret, clientSecretMeta, clientSecretHelpers] =
    useField('client_secret');
  const [username, usernameMeta, usernameHelpers] = useField('username');
  const [password, passwordMeta, passwordHelpers] = useField('password');
  const [satelliteUsername, satelliteUsernameMeta, satelliteUsernameHelpers] =
    useField('satellite_username');
  const [satellitePassword, satellitePasswordMeta, satellitePasswordHelpers] =
    useField('satellite_password');

  const subscriptionModalConfirmation = (value) => {
    subscriptionHelpers.setValue(value);
    subscriptionMethodHelpers.setValue(isSelected);

    manifestHelpers.setValue(null);
    manifestFilenameHelpers.setValue('');

    if (isSelected !== 'selectSubscription') {
      clientIdHelpers.setValue(clientIdMeta.initialValue);
      clientSecretHelpers.setValue(clientSecretMeta.initialValue);
    }

    if (isSelected !== 'selectUsernamePassword') {
      usernameHelpers.setValue(usernameMeta.initialValue);
      passwordHelpers.setValue(passwordMeta.initialValue);
    }

    if (isSelected !== 'selectRedHatSatellite') {
      satelliteUsernameHelpers.setValue(satelliteUsernameMeta.initialValue);
      satellitePasswordHelpers.setValue(satellitePasswordMeta.initialValue);
    }
  };
  return (
    <Flex
      direction={{ default: 'column' }}
      spaceItems={{ default: 'spaceItemsMd' }}
      alignItems={{ default: 'alignItemsBaseline' }}
    >
      {!hasValidKey && (
        <>
          <b>
            {t`Welcome to Red Hat Ansible Automation Platform!
              Please complete the steps below to activate your subscription.`}
          </b>
          <p>
            {t`If you do not have a subscription, you can visit
            Red Hat to obtain a trial subscription.`}
          </p>
          <Button
            aria-label={t`Request subscription`}
            component="a"
            href={LICENSELINK}
            ouiaId="request-subscription-button"
            target="_blank"
            variant="secondary"
            rel="noopener noreferrer"
          >
            {t`Request subscription`}
          </Button>
          <Divider />
        </>
      )}
      <p>{t`Select your Ansible Automation Platform subscription to use.`}</p>
      <ToggleGroup>
        <ToggleGroupItem
          text={t`Subscription manifest`}
          isSelected={isSelected === 'uploadManifest'}
          onChange={() => setIsSelected('uploadManifest')}
          id="subscription-manifest"
        />
        <ToggleGroupItem
          text={t`Service Account`}
          isSelected={isSelected === 'selectSubscription'}
          onChange={() => setIsSelected('selectSubscription')}
          id="service-account"
        />
        <ToggleGroupItem
          text={t`Username and Password`}
          isSelected={isSelected === 'selectUsernamePassword'}
          onChange={() => setIsSelected('selectUsernamePassword')}
          id="username-password"
        />
        <ToggleGroupItem
          text={t`Red Hat Satellite`}
          isSelected={isSelected === 'selectRedHatSatellite'}
          onChange={() => setIsSelected('selectRedHatSatellite')}
          id="red-hat-satellite"
        />
      </ToggleGroup>
      {isSelected === 'uploadManifest' && (
        <>
          <p>
            <Trans>
              Upload a Red Hat Subscription Manifest containing your
              subscription. To generate your subscription manifest, go to{' '}
              <Button
                component="a"
                href="https://access.redhat.com/management/subscription_allocations"
                variant="link"
                target="_blank"
                ouiaId="subscription-allocations-link"
                rel="noopener noreferrer"
                isInline
              >
                subscription allocations
              </Button>{' '}
              on the Red Hat Customer Portal.
            </Trans>
          </p>
          <FileUploadField
            fieldId="subscription-manifest"
            validated={manifestMeta.error ? 'error' : 'default'}
            helperTextInvalid={t`Invalid file format. Please upload a valid Red Hat Subscription Manifest.`}
            label={t`Red Hat subscription manifest`}
            helperText={t`Upload a .zip file`}
            labelIcon={
              <Popover
                content={
                  <Trans>
                    A subscription manifest is an export of a Red Hat
                    Subscription. To generate a subscription manifest, go to{' '}
                    <Button
                      component="a"
                      href="https://access.redhat.com/management/subscription_allocations"
                      variant="link"
                      target="_blank"
                      rel="noopener noreferrer"
                      isInline
                      ouiaId="subscription-allocations-link"
                    >
                      access.redhat.com
                    </Button>
                    . For more information, see the{' '}
                    <Button
                      component="a"
                      href={`${getDocsBaseUrl(
                        config
                      )}/html/userguide/import_license.html`}
                      variant="link"
                      target="_blank"
                      rel="noopener noreferrer"
                      ouiaId="import-license-link"
                      isInline
                    >
                      User Guide
                    </Button>
                    .
                  </Trans>
                }
              />
            }
          >
            <FileUpload
              id="upload-manifest"
              value={manifest.value}
              filename={manifestFilename.value}
              browseButtonText={t`Browse`}
              isDisabled={!config?.me?.is_superuser}
              dropzoneProps={{
                accept: '.zip',
                onDropRejected: () => manifestHelpers.setError(true),
              }}
              onChange={(value, filename) => {
                if (!value) {
                  manifestHelpers.setValue(null);
                  manifestFilenameHelpers.setValue('');
                  clientIdHelpers.setValue(clientIdMeta.initialValue);
                  clientSecretHelpers.setValue(clientSecretMeta.initialValue);
                  usernameHelpers.setValue(usernameMeta.initialValue);
                  passwordHelpers.setValue(passwordMeta.initialValue);
                  satelliteUsernameHelpers.setValue(
                    satelliteUsernameMeta.initialValue
                  );
                  satellitePasswordHelpers.setValue(
                    satellitePasswordMeta.initialValue
                  );
                  subscriptionMethodHelpers.setValue(null);
                  return;
                }

                try {
                  const raw = new FileReader();
                  raw.readAsBinaryString(value);
                  raw.onload = () => {
                    const rawValue = btoa(raw.result);
                    manifestHelpers.setValue(rawValue);
                    manifestFilenameHelpers.setValue(filename);
                  };
                  subscriptionMethodHelpers.setValue('uploadManifest');
                  subscriptionHelpers.setValue(null);
                } catch (err) {
                  manifestHelpers.setError(err);
                }
              }}
            />
          </FileUploadField>
        </>
      )}
      {isSelected === 'selectSubscription' && (
        <>
          {TabParagraph({ isSelected })}
          <Flex
            direction={{ default: 'column', md: 'row' }}
            spaceItems={{ default: 'spaceItemsMd' }}
            alignItems={{ md: 'alignItemsFlexEnd' }}
            fullWidth={{ default: 'fullWidth' }}
          >
            <FormField
              id="client-id-field"
              label={t`Client ID`}
              name="client_id"
              type="text"
              isDisabled={!config.me.is_superuser}
            />
            <PasswordField
              id="client-secret-field"
              name="client_secret"
              label={t`Client secret`}
              isDisabled={!config.me.is_superuser}
            />
            <Button
              aria-label={t`Get subscriptions`}
              ouiaId="subscription-modal-button"
              onClick={toggleModal}
              style={{ maxWidth: 'fit-content' }}
              isDisabled={!(clientId.value && clientSecret.value)}
            >
              {t`Get subscription`}
            </Button>
            {isModalOpen && (
              <SubscriptionModal
                subscriptionCreds={{
                  clientId: clientId.value,
                  clientSecret: clientSecret.value,
                }}
                selectedSubscription={
                  subscriptionMethod.value === 'selectSubscription'
                    ? subscription?.value
                    : null
                }
                onClose={closeModal}
                onConfirm={subscriptionModalConfirmation}
              />
            )}
          </Flex>
          {subscription.value && (
            <SelectedSubscription
              subscription={subscription}
              subscriptionHelpers={subscriptionHelpers}
              subscriptionMethodHelpers={subscriptionMethodHelpers}
            />
          )}
        </>
      )}
      {isSelected === 'selectUsernamePassword' && (
        <>
          {TabParagraph({ isSelected })}
          <Flex
            direction={{ default: 'column', md: 'row' }}
            spaceItems={{ default: 'spaceItemsMd' }}
            alignItems={{ md: 'alignItemsFlexEnd' }}
            fullWidth={{ default: 'fullWidth' }}
          >
            <FormField
              id="username-field"
              label={t`Username`}
              name="username"
              type="text"
              isDisabled={!config.me.is_superuser}
            />
            <PasswordField
              id="password-field"
              name="password"
              label={t`Password`}
              isDisabled={!config.me.is_superuser}
            />
            <Button
              aria-label={t`Get subscriptions`}
              ouiaId="subscription-modal-button"
              onClick={toggleModal}
              style={{ maxWidth: 'fit-content' }}
              isDisabled={!(username.value && password.value)}
            >
              {t`Get subscription`}
            </Button>
            {isModalOpen && (
              <SubscriptionModal
                subscriptionCreds={{
                  username: username.value,
                  password: password.value,
                }}
                selectedSubscription={
                  subscriptionMethod.value === 'selectUsernamePassword'
                    ? subscription?.value
                    : null
                }
                onClose={closeModal}
                onConfirm={subscriptionModalConfirmation}
              />
            )}
          </Flex>
          {subscription.value && (
            <SelectedSubscription
              subscription={subscription}
              subscriptionHelpers={subscriptionHelpers}
              subscriptionMethodHelpers={subscriptionMethodHelpers}
            />
          )}
        </>
      )}
      {isSelected === 'selectRedHatSatellite' && (
        <>
          {TabParagraph({ isSelected })}
          <Flex
            direction={{ default: 'column', md: 'row' }}
            spaceItems={{ default: 'spaceItemsMd' }}
            alignItems={{ md: 'alignItemsFlexEnd' }}
            fullWidth={{ default: 'fullWidth' }}
          >
            <FormField
              id="satellite-username-field"
              label={t`Satellite username`}
              name="satellite_username"
              type="text"
              isDisabled={!config.me.is_superuser}
            />
            <PasswordField
              id="satellite-password-field"
              name="satellite_password"
              label={t`Satellite password`}
              isDisabled={!config.me.is_superuser}
            />
            <Button
              aria-label={t`Get subscriptions`}
              ouiaId="subscription-modal-button"
              onClick={toggleModal}
              style={{ maxWidth: 'fit-content' }}
              isDisabled={!(satelliteUsername.value && satellitePassword.value)}
            >
              {t`Get subscription`}
            </Button>
            {isModalOpen && (
              <SubscriptionModal
                subscriptionCreds={{
                  username: satelliteUsername.value,
                  password: satellitePassword.value,
                }}
                selectedSubscription={
                  subscriptionMethod.value === 'selectRedHatSatellite'
                    ? subscription?.value
                    : null
                }
                onClose={closeModal}
                onConfirm={subscriptionModalConfirmation}
              />
            )}
          </Flex>
          {subscription.value && (
            <SelectedSubscription
              subscription={subscription}
              subscriptionHelpers={subscriptionHelpers}
              subscriptionMethodHelpers={subscriptionMethodHelpers}
            />
          )}
        </>
      )}
    </Flex>
  );
}
export default SubscriptionStep;

export function TabParagraph({ isSelected }) {
  const messages = {
    selectUsernamePassword: t`Provide your Red Hat credentials below and you can choose from a list of your available subscriptions. The credentials you use will be stored for future use in retrieving renewal or expanded subscriptions.`,
    selectRedHatSatellite: t`Provide your Red Hat Satellite credentials below and you can choose from a list of your available subscriptions. The credentials you use will be stored for future use in retrieving renewal or expanded subscriptions.`,
    selectSubscription: t`Provide your service account credentials below and you can choose from a list of your available subscriptions. The credentials you use will be stored for future use in retrieving renewal or expanded subscriptions.`,
  };

  return (
    <p>
      {messages[isSelected]}
      {isSelected === 'selectSubscription' && (
        <>
          {' '}
          <Trans>
            Service account credentials can be found{' '}
            <a
              href="https://console.redhat.com/iam/service-accounts"
              target="_blank"
              rel="noreferrer"
            >
              here on console.redhat.com
            </a>
          </Trans>
        </>
      )}
    </p>
  );
}

export function SelectedSubscription({
  subscription,
  subscriptionHelpers,
  subscriptionMethodHelpers,
}) {
  return (
    <Flex
      id="selected-subscription"
      alignSelf={{ default: 'alignSelfFlexStart' }}
      spaceItems={{ default: 'spaceItemsMd' }}
    >
      <b>{t`Selected`}</b>
      <FlexItem>
        <i>{subscription?.value?.subscription_name}</i>
        <Tooltip
          trigger="mouseenter focus click"
          content={t`Clear subscription`}
        >
          <Button
            onClick={() => {
              subscriptionHelpers.setValue(null);
              subscriptionMethodHelpers.setValue(null);
            }}
            variant="plain"
            aria-label={t`Clear subscription selection`}
            ouiaId="clear-subscription-selection"
          >
            <TimesIcon />
          </Button>
        </Tooltip>
      </FlexItem>
    </Flex>
  );
}
